import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNav } from "../../components/NavContext";
import { daysOfWeek } from "../Utils/Constants";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function DoctorPage({ doctorId }) {
    const params = useParams();
    const id = doctorId || params.doctorId;
    const API_URL = process.env.REACT_APP_API_URL;
    const DOMAIN = process.env.REACT_APP_DOMAIN;
    const DOCTOR_PROFILE_PICTURE_URL = process.env.REACT_APP_STORAGE_DOCTOR_PROFILE_PICTURE_URL;
    axios.defaults.withCredentials = true;
    const [formData, setFormData] = useState({
        // Personal
        full_name: "",
        username: "",
        password: "",
        gender: "",
        // Professional
        field_id: "",
        brief: "",
        info: "",
        license_number: "",
        license_date: "",
        // Media
        profile_picture: "",
        // Clinic
        clinic_address: "",
        clinic_latitude: "",
        clinic_longitude: "",
        phone_number: "",
        open_days: [],
        open_day_times: {},
        // Availability & Fees
        is_phone: true,
        is_clinic: false,
        consultation_fee: "",
        clinic_consultation_fee: "",
        // Social
        facebook_url: "",
        instagram_url: "",
        tiktok_url: "",
        snapchat_url: "",
        telegram_url: "",
    });

    const [clinicLocation, setClinicLocation] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [mapKey, setMapKey] = useState(0);
    const [locationLoading, setLocationLoading] = useState(false);

    const [fields, setFields] = useState([]);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const { addLink } = useNav();

    useEffect(() => {
      addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
      addLink("اضافة طبيب", `${DOMAIN}control/doctor-create`);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addLink]);

    // Hour list options (00:00, 00:30, ..., 23:30)
    const HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2);
        const m = i % 2 === 0 ? '00' : '30';
        return `${String(h).padStart(2, '0')}:${m}`;
    });

    // Normalize backend time values to HH:MM (drop seconds, pad hour/min)
    const toHHMM = (val) => {
        if (!val) return '';
        const parts = String(val).split(':');
        const h = parts[0] ? String(parseInt(parts[0], 10)).padStart(2, '0') : '00';
        const m = parts[1] ? String(parseInt(parts[1], 10)).padStart(2, '0') : '00';
        return `${h}:${m}`;
    };

    useEffect(() => {
        const fetchDoctor = async () => {
            try {
                const response = await axios.get(`${API_URL}admin/doctor/id/${id}`, {});
                // Normalize open_days: backend returns objects [{day_name, opening_time, closing_time}]
                // but this UI expects an array of strings ["Monday", ...] for checkboxes
                const data = response.data || {};
                let openDays = [];
                let timesMap = {};
                if (Array.isArray(data.open_days)) {
                    if (data.open_days.length > 0) {
                        if (typeof data.open_days[0] === 'string') {
                            openDays = data.open_days;
                            // if legacy times provided
                            const legacy = data.open_day_times || {};
                            timesMap = Object.keys(legacy).reduce((acc, k) => {
                                acc[k] = {
                                    opening_time: toHHMM(legacy[k]?.opening_time || ''),
                                    closing_time: toHHMM(legacy[k]?.closing_time || ''),
                                };
                                return acc;
                            }, {});
                        } else {
                            openDays = data.open_days.map(d => d?.day_name).filter(Boolean);
                            timesMap = data.open_days.reduce((acc, d) => {
                                if (d && d.day_name) {
                                    acc[d.day_name] = {
                                        opening_time: toHHMM(d.opening_time || ''),
                                        closing_time: toHHMM(d.closing_time || ''),
                                    };
                                }
                                return acc;
                            }, {});
                        }
                    }
                }
                const merged = { ...data, open_days: openDays, open_day_times: timesMap };
                setFormData(merged);
                // Initialize clinicLocation from existing coordinates if present
                if (merged.clinic_latitude && merged.clinic_longitude) {
                    const lat = parseFloat(merged.clinic_latitude);
                    const lng = parseFloat(merged.clinic_longitude);
                    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                        setClinicLocation({ latitude: lat, longitude: lng });
                        setMapKey((prev) => prev + 1);
                    }
                }
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    navigate("/control/login");
                }
                setMessage({ type: "error", text: "Failed to load doctor information." });
            } finally {
                setLoading(false);
            }
        };

        const fetchFields = async () => {
            try {
                const response = await axios.get(`${API_URL}fields/list`, {});
                setFields(response.data);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    navigate("/control/login");
                }
                setMessage({ type: "error", text: "Failed to load fields information." });
            }
        };

        fetchDoctor();
        fetchFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, API_URL]);

    // Try to get current device location once (for initial map center when no clinic location)
    useEffect(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const loc = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
                setCurrentLocation(loc);
                setMapKey((prev) => prev + 1);
            },
            () => {},
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000,
            }
        );
    }, []);

    const handleChange = (e) => {
        const { name, checked, value, type, files } = e.target;

        setFormData((prevData) => {
            let newFormData = { ...prevData };

            if (name === "profile_picture") {
                newFormData.profile_picture = files[0];
            } else if (name === "is_clinic" || name === "is_phone") {
                newFormData[name] = checked;
            } else if (type === "checkbox") {
                // Day toggle: maintain open_days and open_day_times together
                const isDayCheckbox = daysOfWeek.some(d => d.english === name);
                if (isDayCheckbox) {
                    const currentlyChecked = prevData.open_days.includes(name);
                    if (checked && !currentlyChecked) {
                        newFormData.open_days = [...prevData.open_days, name];
                        const times = { ...(prevData.open_day_times || {}) };
                        if (!times[name]) times[name] = { opening_time: '', closing_time: '' };
                        newFormData.open_day_times = times;
                    } else if (!checked && currentlyChecked) {
                        newFormData.open_days = prevData.open_days.filter(day => day !== name);
                        const times = { ...(prevData.open_day_times || {}) };
                        if (times[name]) delete times[name];
                        newFormData.open_day_times = times;
                    }
                } else {
                    // Other checkboxes if any
                    newFormData[name] = checked;
                }
            } else {
                newFormData[name] = value;
            }

            return newFormData;
        });
    };

    // Change per-day opening/closing time
    const handleTimeChange = (dayEnglish, field, timeValue) => {
        setFormData((prev) => {
            const times = { ...(prev.open_day_times || {}) };
            if (!times[dayEnglish]) times[dayEnglish] = { opening_time: '', closing_time: '' };
            times[dayEnglish] = { ...times[dayEnglish], [field]: timeValue };
            return { ...prev, open_day_times: times };
        });
    };

    // OpenStreetMap component with click handler
    const MapClickHandler = ({ onMapClick }) => {
        useMapEvents({
            click: (e) => {
                const { lat, lng } = e.latlng;
                onMapClick({ latitude: lat, longitude: lng });
            },
        });
        return null;
    };

    const OpenStreetMapComponent = ({ center, clinicLocation, onMapClick }) => {
        const mapCenter = clinicLocation 
            ? [clinicLocation.latitude, clinicLocation.longitude]
            : [center.lat, center.lng];

        return (
            <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onMapClick={onMapClick} />
                {clinicLocation && (
                    <Marker 
                        position={[clinicLocation.latitude, clinicLocation.longitude]}
                        title="Clinic Location"
                    />
                )}
            </MapContainer>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: "", text: "" });

        const formDataToSend = new FormData();
        const skipIfEmpty = new Set([
            'clinic_latitude',
            'clinic_longitude',
            'consultation_fee',
            'clinic_consultation_fee',
            'license_number',
            'license_date',
        ]);

        Object.keys(formData).forEach((key) => {
            const val = formData[key];

            // Send open_days as array items
            if (key === 'open_days' && Array.isArray(val)) {
                if (val.length === 0) return; // nothing to send
                val.forEach((day) => formDataToSend.append('open_days[]', day));
                return;
            }

            // Send booleans as 1/0 for consistency
            if (key === 'is_clinic' || key === 'is_phone') {
                formDataToSend.append(key, val ? 1 : 0);
                return;
            }

            // Normalize social links to empty string when null/undefined/'null'
            if ((key === 'facebook_url' || key === 'instagram_url' || key === 'tiktok_url' || key === 'snapchat_url' || key === 'telegram_url')) {
                formDataToSend.append(key, (!val || val === 'null') ? '' : val);
                return;
            }

            // Avoid sending empty strings for nullable numeric/date fields
            if (skipIfEmpty.has(key) && (val === '' || val === undefined || val === null)) {
                return; // skip appending, backend will treat as missing (nullable)
            }

            // Files and other fields
            formDataToSend.append(key, val);
        });

        // Add open_day_times as nested fields: open_day_times[Day][opening_time]
        if (formData.open_day_times && typeof formData.open_day_times === 'object') {
            daysOfWeek.forEach(({ english }) => {
                if (formData.open_days.includes(english) && formData.open_day_times[english]) {
                    const ot = formData.open_day_times[english].opening_time || '';
                    const ct = formData.open_day_times[english].closing_time || '';
                    if (ot !== '') formDataToSend.append(`open_day_times[${english}][opening_time]`, ot);
                    if (ct !== '') formDataToSend.append(`open_day_times[${english}][closing_time]`, ct);
                }
            });
        }
        try {
            await axios.post(`${API_URL}admin/doctor/update/id/${id}`, formDataToSend, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            setMessage({ type: "success", text: "Doctor updated successfully!" });
        } catch (err) {
            setMessage({ type: "error", text: "Failed to update doctor: " + err.response.data.message });
        }
    };

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center vh-100">Loading...</div>;
    }

    return (
        <div className="container mt-4" dir="rtl">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <header className="mb-4 text-center">
                        <h1 className="h3 mb-1">بيانات الطبيب</h1>
                        <div className="text-muted">تحرير معلومات الطبيب وتقسيمها إلى أقسام واضحة</div>
                    </header>
                    {message.text && (
                        <div className={`alert ${message.type === "error" ? "alert-danger" : "alert-success"}`}>
                            {message.text}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="">
                        {/* Personal & Account */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">المعلومات الشخصية والحساب</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">الاسم الكامل</label>
                                        <input type="text" name="full_name" value={formData.full_name || ""} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">اسم المستخدم</label>
                                        <input type="text" name="username" value={formData.username || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">كلمة المرور (اتركه فارغًا للتثبيت)</label>
                                        <input type="password" name="password" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">الجنس</label>
                                        <select name="gender" value={formData.gender || ""} onChange={handleChange} className="form-select">
                                            <option value="">اختر الجنس</option>
                                            <option value="male">ذكر</option>
                                            <option value="female">أنثى</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">التخصص</label>
                                        <select name="field_id" value={formData.field_id || ""} onChange={handleChange} className="form-select">
                                            <option value="">اختر التخصص</option>
                                            {fields.map((field) => (
                                                <option key={field.field_id} value={field.field_id}>{field.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Professional */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">المعلومات المهنية</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">نبذة</label>
                                        <textarea name="brief" value={formData.brief || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">معلومات إضافية</label>
                                        <textarea name="info" value={formData.info || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">رقم الرخصة</label>
                                        <input type="text" name="license_number" value={formData.license_number || ""} onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">تاريخ الرخصة</label>
                                        <input type="date" name="license_date" value={formData.license_date || ""} onChange={handleChange} className="form-control" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Clinic */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">العيادة</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label">عنوان العيادة</label>
                                        <textarea name="clinic_address" value={formData.clinic_address || ""} onChange={handleChange} className="form-control" rows={2} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">رقم الهاتف</label>
                                        <input type="text" name="phone_number" value={formData.phone_number || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="form-label d-block mb-2">موقع العيادة على الخريطة</label>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                disabled={locationLoading}
                                                onClick={() => {
                                                    if (!navigator.geolocation) return;
                                                    setLocationLoading(true);
                                                    navigator.geolocation.getCurrentPosition(
                                                        (position) => {
                                                            const loc = {
                                                                latitude: position.coords.latitude,
                                                                longitude: position.coords.longitude,
                                                            };
                                                            setClinicLocation(loc);
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                clinic_latitude: String(loc.latitude),
                                                                clinic_longitude: String(loc.longitude),
                                                            }));
                                                            setLocationLoading(false);
                                                            setMapKey((prev) => prev + 1);
                                                        },
                                                        () => {
                                                            setLocationLoading(false);
                                                        },
                                                        {
                                                            enableHighAccuracy: true,
                                                            timeout: 10000,
                                                            maximumAge: 0,
                                                        }
                                                    );
                                                }}
                                            >
                                                {locationLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        جاري التحديد...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-crosshairs me-1"></i>
                                                        استخدام الموقع الحالي
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        <small className="text-muted text-end">انقر على الخريطة لاختيار موقع العيادة</small>
                                    </div>
                                    <div style={{ height: "300px", border: "2px solid #dee2e6", borderRadius: "8px", overflow: "hidden" }}>
                                        {(clinicLocation || currentLocation) ? (
                                            <OpenStreetMapComponent
                                                key={mapKey}
                                                center={clinicLocation ? { lat: clinicLocation.latitude, lng: clinicLocation.longitude } : { lat: currentLocation.latitude, lng: currentLocation.longitude }}
                                                clinicLocation={clinicLocation}
                                                onMapClick={(loc) => {
                                                    setClinicLocation(loc);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        clinic_latitude: String(loc.latitude),
                                                        clinic_longitude: String(loc.longitude),
                                                    }));
                                                    setMapKey((prev) => prev + 1);
                                                }}
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                                <div className="text-center text-muted">
                                                    <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                                                    <p>لم يتم تحديد موقع العيادة بعد</p>
                                                    <p>استخدم زر "استخدام الموقع الحالي" أو انقر على الخريطة لتحديد الموقع</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-2 text-end">
                                        <small className="text-muted">
                                            {formData.clinic_latitude && formData.clinic_longitude
                                                ? `الموقع الحالي: ${Number(formData.clinic_latitude).toFixed(6)}, ${Number(formData.clinic_longitude).toFixed(6)}`
                                                : "لم يتم تحديد موقع"}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Availability & Fees */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">التوافر والرسوم</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">سعر الاستشارة أونلاين</label>
                                        <input type="text" name="consultation_fee" value={formData.consultation_fee || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">سعر الاستشارة في العيادة</label>
                                        <input type="text" name="clinic_consultation_fee" value={formData.clinic_consultation_fee || ""} dir="ltr" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6 d-flex align-items-center justify-content-between">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="is_phone" name="is_phone" checked={!!formData.is_phone} onChange={handleChange} />
                                            <label className="form-check-label" htmlFor="is_phone">تفعيل استشارة هاتف</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="is_clinic" name="is_clinic" checked={!!formData.is_clinic} onChange={handleChange} />
                                            <label className="form-check-label" htmlFor="is_clinic">تفعيل حجز العيادة</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Open Days */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">أيام الدوام</div>
                            <div className="card-body">
                                <div className="row g-2">
                                    {daysOfWeek.map(({ english, arabic }) => (
                                        <div className="col-6 col-md-3" key={english}>
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" id={`day_${english}`} name={english} checked={formData.open_days.includes(english)} onChange={handleChange} />
                                                <label className="form-check-label" htmlFor={`day_${english}`}>{arabic}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Opening hours per selected day */}
                                {formData.open_days.length > 0 && (
                                    <div className="mt-3">
                                        <div className="row g-3 align-items-center">
                                            {formData.open_days.map((day) => {
                                                const arabicLabel = (daysOfWeek.find(d => d.english === day) || {}).arabic || day;
                                                const times = formData.open_day_times?.[day] || { opening_time: '', closing_time: '' };
                                                return (
                                                    <div className="col-12" key={`times_${day}`}>
                                                        <div className="row g-2 align-items-center">
                                                            <div className="col-12 col-md-3 d-flex align-items-center">
                                                                <span className="fw-semibold">{arabicLabel}</span>
                                                            </div>
                                                            <div className="col-6 col-md-4">
                                                                <label className="form-label mb-1">من</label>
                                                                <select className="form-select" value={times.opening_time || ''} onChange={(e) => handleTimeChange(day, 'opening_time', e.target.value)}>
                                                                    <option value="">—</option>
                                                                    {HOUR_OPTIONS.map((hhmm) => (
                                                                        <option key={`ot_${day}_${hhmm}`} value={hhmm}>{hhmm}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div className="col-6 col-md-4">
                                                                <label className="form-label mb-1">إلى</label>
                                                                <select className="form-select" value={times.closing_time || ''} onChange={(e) => handleTimeChange(day, 'closing_time', e.target.value)}>
                                                                    <option value="">—</option>
                                                                    {HOUR_OPTIONS.map((hhmm) => (
                                                                        <option key={`ct_${day}_${hhmm}`} value={hhmm}>{hhmm}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Social & Media */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-light fw-semibold">الروابط والصورة</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">رابط الفيسبوك</label>
                                        <input type="url" name="facebook_url" value={formData.facebook_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://facebook.com/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">رابط الانستغرام</label>
                                        <input type="url" name="instagram_url" value={formData.instagram_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://instagram.com/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">رابط تيك توك</label>
                                        <input type="url" name="tiktok_url" value={formData.tiktok_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://tiktok.com/@username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">رابط سناب شات</label>
                                        <input type="url" name="snapchat_url" value={formData.snapchat_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://snapchat.com/add/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">رابط تيليغرام</label>
                                        <input type="url" name="telegram_url" value={formData.telegram_url || ""} dir="ltr" onChange={handleChange} className="form-control" placeholder="https://t.me/username" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">الصورة الشخصية</label>
                                        <input type="file" name="profile_picture" accept="image/*" onChange={handleChange} className="form-control" />
                                    </div>
                                    <div className="col-md-6 d-flex align-items-center">
                                        {formData.profile_picture && typeof formData.profile_picture === 'string' && (
                                            <img src={`${DOCTOR_PROFILE_PICTURE_URL}${formData.profile_picture}`} alt="Doctor Profile" className="rounded border" style={{ maxWidth: 120, maxHeight: 120, objectFit: 'cover' }} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-grid">
                            <button type="submit" className="btn btn-primary">حفظ التعديلات</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
